import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-semibold ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(232,0,45,0.28)] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        /* Company red CTA — white label/icon; rectangular with 8px rounded corners */
        default:
          'rounded-[8px] !text-white [&_svg]:!text-white bg-[#E8002D] hover:bg-[#c90026] shadow-brand-btn',
        destructive:
          'rounded-[8px] bg-[#E8002D] !text-white [&_svg]:!text-white hover:bg-[#c90026]',
        outline:
          'rounded-[8px] border border-gray-200 bg-white text-gray-700 hover:bg-brand-50 hover:border-brand-600 hover:text-brand-700 dark:border-neutral-600 dark:bg-[var(--color-surface)] dark:text-neutral-200 dark:hover:bg-neutral-800 dark:hover:text-white',
        secondary:
          'rounded-[8px] bg-neutral-100 text-neutral-700 font-medium hover:bg-neutral-200 dark:bg-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-600',
        ghost: 'rounded-[8px] font-medium hover:bg-brand-50 hover:text-brand-800 dark:hover:bg-neutral-800 dark:hover:text-white',
        link: 'text-brand-600 underline-offset-4 hover:underline font-medium',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm:      'h-9 rounded-[8px] px-3 text-sm-ui',
        lg:      'h-11 rounded-[8px] px-8',
        icon:    'h-10 w-10 rounded-[8px]',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?:  boolean;
  loading?:  boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        disabled={disabled ?? loading}
        {...props}
      >
        {loading && <Loader2 className="animate-spin" />}
        {children}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
